import FxxlConfigs from '@fxxl/configs';
import FxxlError from '@fxxl/error';

export default class FxxlCore extends FxxlConfigs {
    constructor() {
        super();
        this.boundFirstActionEvents = this.firstActionEvent.bind(this);
        this.firstActionTriggered = false;
        this.firstActionCachedIsTriggered = false;
    }
    setDefaultEvents() {
        this.setupFirstActionEvent();
        this.setupFirstActionCachedEvent();
    }


    /**
     * Dieser JavaScript-Code definiert eine statische Methode namens "setStorage", die es
     * ermöglicht, Daten in den lokalenSpeicher des Browsers zu schreiben.
     *
     * Die Methode nimmt drei Parameter entgegen: "storageKey" ist der Schlüssel, unter
     * dem die Daten im lokalen Speicher gespeichert werden sollen. "data" ist
     * der Wert, der gespeichert werden soll.
     * Wenn "expire" den Wert Null hat, ist die Daten immer gültig. Wenn jedoch ein Timestamp
     * eingesetzt wird, ist die Gültigkeit der Daten auf den angegebenen Timestamp beschränkt.
     *
     * z.B.:
     * Berechne den Zeitstempel für einen Tag später (in Millisekunden)
     * Timestamp = Date.now() + (24 * 60 * 60 * 1000);
     *
     * @param {string} storageKey
     * @param {any} data
     * @param {number|null} expire
     */
    static setStorage(storageKey, data, expire = null) {
        const baseData = {data: null, expire: null};

        // Base Data
        baseData.data = data;
        baseData.expire = expire;

        localStorage.setItem(storageKey, JSON.stringify(baseData));
    }

    /**
     * Diese Methode entfernt alle Daten aus dem localStorage, deren Key mit dem übergebenen Präfix beginnt.
     * z.B.: Wenn das Präfix "fxxl_" ist, werden alle gespeicherten Daten, die mit "fxxl_" beginnen, gelöscht.
     *
     * @param prefix
     */
    static removeStoragesWithPrefix(prefix) {
        if (typeof prefix === 'string' && prefix.length > 0) {
            const keys = Object.keys(localStorage);
            keys.forEach((key) => {
                if (key.startsWith(prefix)) {
                    localStorage.removeItem(key);
                }
            });
        }
    }

    /**
     * Dieser JavaScript-Code definiert eine statische Methode namens "getStorage", die
     * es ermöglicht, Daten aus dem lokalen Speicher des Browsers abzurufen.
     *
     * Wenn ein Fehler auftritt, wird "undefined" ausgegeben.
     *
     * @param {string} storageKey
     * @returns {any}
     */
    static getStorage(storageKey) {
        let result = undefined;
        let baseData = localStorage.getItem(storageKey);

        try {
            baseData = JSON.parse(baseData);
        } catch {
            baseData = {};
        }

        // Es wird überprüft, ob die Daten noch gültig sind
        if (!baseData?.expire || baseData?.expire >= Date.now()) {
            result = baseData?.data;
        }

        return result;
    }

    setupFirstActionCachedEvent() {
        let isFirstActionCached = false;
        const data = FxxlCore.getStorage(FxxlCore.getConfig()?.storageKeys.firstAction);
        if (data) {
            isFirstActionCached = (data?.date !== undefined);
        }
        if (isFirstActionCached) {
            setTimeout(() => FxxlCore.eventEmitter(FxxlCore.getConfig().eventNames.firstActionCached).emit(), 1000);
            this.firstActionCachedIsTriggered = true;
        }
    }

    setupFirstActionEvent() {
        if (document.readyState !== 'complete' && document.readyState !== 'interactive') {
            document.addEventListener('DOMContentLoaded', () => {
                this.emitReadyEvent();
                this.setRemoveFirstActionEvent();
            });
        } else {
            this.emitReadyEvent();
            this.setRemoveFirstActionEvent();
        }
    }

    emitReadyEvent() {
        requestAnimationFrame(() => {
            FxxlCore.eventEmitter(FxxlCore.getConfig().eventNames.ready).emit(null, document)
        });
    }

    /**
     *
     * @param {boolean} setEvent
     */
    setRemoveFirstActionEvent(setEvent = true) {
        let methodName;
        if (setEvent === true) {
            methodName = 'addEventListener';
        } else {
            methodName = 'removeEventListener';
        }

        setTimeout(() => {
            const targetElement = document.documentElement;
            targetElement[methodName]('mousemove', this.boundFirstActionEvents);
            targetElement[methodName]('keydown', this.boundFirstActionEvents);
            targetElement[methodName]('touchstart', this.boundFirstActionEvents);
        }, 1000);
    }

    firstActionEvent(e) {
        if (this.firstActionTriggered !== true) {
            this.firstActionTriggered = true;
            // Set Cache
            FxxlCore.setStorage(FxxlCore.getConfig()?.storageKeys.firstAction, {date: `${new Date()}`});
            // Send Event
            FxxlCore.eventEmitter(FxxlCore.getConfig().eventNames.firstAction).emit();
            if (this.firstActionCachedIsTriggered === false) {
                FxxlCore.eventEmitter(FxxlCore.getConfig().eventNames.firstActionCached).emit();
            }

            // Remove event
            this.setRemoveFirstActionEvent(false);
        }
    }


    /**
     * Dieser JavaScript-Code definiert eine statische Methode namens "eventEmitter", die
     * ein Objekt zurückgibt, das eine "emit"-Methode enthält.
     * Diese Methode ermöglicht es, ein Ereignis (Event) auszulösen
     * und optional einen Wert als Parameter zu übergeben.
     *
     * @param {string} eventName
     * @returns {{emit: ((function(*, HTMLElement=): (boolean))|*)}}
     */
    static eventEmitter(eventName) {
        return {
            emit: (value = null, element = window) => {
                let result = false;
                const foundDispatchEvent = (element?.dispatchEvent !== undefined);
                if (typeof element === 'object' && foundDispatchEvent === true) {
                    const event = new CustomEvent(eventName, {
                        detail: value,
                    });
                    element.dispatchEvent(event);
                    if (FxxlCore.windowVariable('fxxlEventHistory').includes(eventName) === false) {
                        FxxlCore.windowVariable('fxxlEventHistory').push(eventName);
                    }
                    result = true;
                }
                if (foundDispatchEvent === false) {
                    new FxxlError('"dispatchEvent" ist nicht gefunden');
                }
                return result;
            }
        };
    }

    /**
     * Die Methode "addDelegatedEventListener" erlaubt es DOM-Events via Event Bubbling zu nutzen.
     * Dies stellt ein Equivalent zur JQuery "$(document).on('event', 'selector', function () {}" Funktion dar.
     *
     * Der Vorteil der "Event Delegation" liegt darin,
     * dass sie auch dynamisch hinzugefügte DOM-Elemente handhaben kann,
     * die nach initialen "addEventListener" z.B. via dem OnLoad-Event ins DOM eingefügt wurden.
     *
     * Erklärung: Das eigentliche Event wird anstatt auf einem Element direkt, z.B. auf den <body> gelegt.
     * Wird das Event ausgelöst, wird gefragt wo innerhalb des <body> Bereichs das Event ausgelöst wurde.
     * Passen Selector und Event Target zusammen kann eine Funktion auf diesem Element ausgeführt werden.
     *
     * Hinweis: Diese Methode ruft den Händler ( Parameter ) nur für ein HTML Element auf,
     * nicht für alle Elemente, die den Selektor entsprechen.
     * ( Entspricht aktuell damit nicht vollständing dem JQuery-Äquivalent )
     *
     * Handler Parameter:
     * - Event Target
     * - PointerEvent
     *
     * @param {string} eventType
     * @param {string} selector
     * @param {object} handler
     * @param {object|null} scope
     */
    static addDelegatedEventListener(eventType, selector, handler, scope = null) {
        if (
            typeof eventType    !== 'string'
            || typeof selector  !== 'string'
            || typeof handler   !== 'function'
            || ( scope !== null && !(scope instanceof HTMLElement) )
        ) {
            return;
        }

        (scope || document).addEventListener(eventType, event => {
            const listenedTarget = event?.target.closest(selector);

            if (listenedTarget) {
                handler(listenedTarget, event);
            }
        });
    }

    /**
     * windowVariable
     *
     * @param {string} variableName
     * @param {any} defaultValue
     */
    static windowVariable(variableName, defaultValue = []) {
        if (window[variableName] === undefined) {
            window[variableName] = defaultValue;
        }
        return window[variableName];
    }

    /**
     * waitForEventIfNecessary
     *
     * @param {HTMLElement} element
     * @param {string} eventname
     * @param {Function} eventname
     */
    static waitForEventIfNecessary(element, eventname, callback) {
        if (FxxlCore.windowVariable('fxxlEventHistory').includes(eventname) === false) {
            element.addEventListener(eventname, () => {
                callback();
            });
        } else {
            callback();
        }
    }


    /**
     * Die Methode "lazyloadModuleByView" lädt ein Modul, wenn bestimmte Kriterien erfüllt sind.
     * Sie überprüft, ob das Modul benötigt wird, und gibt eine Promise zurück, die
     * das geladene Modul oder einen Fehler zurückgibt.
     *
     * @param {string} module
     * @param {Array} viewDatas z.B: {presence: string|null, view_type: string|null, view_name: string|null}
     * @param {string|null} loadEventName
     * @param {string} mode
     * @returns {Promise<Object|null>}
     */
    static async lazyloadModuleByView(module, viewDatas = null, loadEventName = null, mode = 'module') {
        const defaultViewData = {
            presence: null,
            view_type: null,
            view_name: null
        };

        let needed = false;

        if (Array.isArray(viewDatas)) {
            viewDatas.forEach((viewData) => {
                const newViewData = Object.assign({}, defaultViewData, viewData);
                const mapPresenceToInternalId = FxxlCore.getConfig().server?.presence?.internal_description_to_id ?? {};
                if (
                    needed !== true
                    && (newViewData.presence === null || Number(mapPresenceToInternalId[newViewData.presence]) === Number(_presence_id))
                    && (newViewData.view_type === null || newViewData.view_type === site_type)
                    && (newViewData.view_name === null || newViewData.view_name === site_name)
                ) {
                    needed = true;
                }
            });
        }

        return new Promise( (resolve, reject) => {
            if (needed) {
                if (loadEventName !== null && FxxlCore.windowVariable('fxxlEventHistory').includes(loadEventName) === false) {
                    window.addEventListener(loadEventName, async () => resolve(await FxxlCore.loadModule(module, mode)), true);
                } else {
                    if (mode === 'module') {
                        resolve(FxxlCore.loadModule(module, mode));
                    } else {
                        FxxlCore.loadModule(module, mode);
                        resolve();
                    }
                }
            } else {
                reject();
            }
        });
    }


    /**
     * Die statische Methode "loadModule" lädt ein Modul entweder
     * als JavaScript-Modul oder als Skript. Das geladene Modul wird zurückgegeben.
     *
     * @param {string} module
     * @param {string} mode
     * @returns {Promise<*>}
     */
    static async loadModule(module, mode) {
        let result;
        if (mode === 'module') {
            result = await import(`@fxxl/${module}`);
            FxxlCore.eventEmitter(`${module}_load`).emit(result);
        } else if (mode === 'script') {
            const body = document.body;

            // Das darf nicht passieren, dass "jsts" nicht gefunden wird. Deshalb sollte der statische Wert als "v1" in Ordnung sein.
            const ts = document?.documentElement?.dataset?.jsts || 'v1';
            // ##################################################################

            const scriptTag = document.createElement('script');
            scriptTag.src = `/custom/module/public/fxxl3/dist/fxxl3.${module}.min.js?ts=${ts}`;
            scriptTag.defer = true;
            body.appendChild(scriptTag);
        }
        return result;
    }


    /**
     * Die Methode "externalUseableObject" erstellt ein Objekt, das von
     * außerhalb der Klasse aufgerufen werden kann.
     * Das Objekt wird als globale Variable mit dem angegebenen Namen im Fenster-Objekt gespeichert.
     *
     * @param {string} name
     * @param {Object|string|number} object
     */
    static externalUseableObject(name, object) {
        window[name] = object;
    }

    /**
     * Es wird überprüft, ob "value" eine Zahl ist.
     *
     * @param {any} value
     * @returns {boolean}
     */
    static isNumeric(value) {
        return Number.isFinite(parseFloat(value));
    }

    /**
     * Die Methode "isTouchDevice" überprüft, ob das Gerät ein Touchscreen hat.
     *
     * @returns {boolean}
     */
    static isTouchDevice() {
        return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
    }

    /**
     * Die Methode "getParameterByName" gibt den Wert eines URL-Parameters zurück.
     *
     * @param name string
     * @param url string
     * @returns {string | boolean}
     */
     getParameterByName(name, url) {
         // canParse() wird nicht in allen Zielbrowsern unterstützt, deswegen wird ein try-catch-Block verwendet
        try {
            const validUrl = new URL(url ?? window.location.href);
            return validUrl.searchParams.get(name) ?? false;
        } catch (error) {
            // Invalid URL
            FxxlError.logError(error);
            return false;
        }
    }

    /**
     *  Build url whit Params
     *  @param {string} baseUrl
     *  @param {Object} addParams
     */
    static buildUrlWithParams(baseUrl, addParams = {}) {

        let currentUrl;
        try {
            currentUrl = new URL(baseUrl, window.location.href);
        } catch (error) {
            new FxxlError('FXXL URL Error: Invalid URL ' + baseUrl);
            return baseUrl;
        }

        for (const [key, value] of Object.entries(addParams)) {
            currentUrl.searchParams.set(key, value);
        }

        return currentUrl.href;
    }

    /**
     *  Works basically like PHPs html_entity_decode.
     *  It takes a string and converts the most used html special characters to its UTF-8 Counterpart.
     *
     *  Note:
     *      - This does not convert Tags
     *      - Because JS does not provide a native list of html special characters,
     *        in order to keep the HTML Entity List as
     *        small as possible, that list is reduced to most used
     *
     *  Maybe TODO:
     *      - htmlEntities could possibly be put in a 060_prototype_enhancement.js or into a new FxxlConstants
     *
     *  References:
     *      - https://wiki.selfhtml.org/wiki/Zeichenreferenz
     *
     *  @param {string} text
     *  @returns {string}
     */
    static decodeHtmlEntities(text) {
        if (typeof text !== 'string') {
            new FxxlError('Fxxl Decode Error: Parameter not string');
            return '';
        }

        // Fixed Entities
        const htmlEntities = [
            // HTML-eigene Zeichen
            { char: '&',  entity: '&amp;'    },  { char: '<', entity: '&lt;'     },  { char: '>', entity: '&gt;'     },
            { char: '"', entity: '&quot;'    },  { char: "'",  entity: '&apos;'  },
            // Latin1-Ergänzung
            { char: '©',  entity: '&copy;'   },  { char: '®', entity: '&reg;'    },  { char: '°', entity: '&deg;'    },
            { char: '±',  entity: '&plusmn;' },  { char: '²', entity: '&sup2;'   },  { char: '³', entity: '&sup3;'   },
            { char: '´',  entity: '&acute;'  },  { char: '×', entity: '&times;'  },  { char: '÷', entity: '&divide;' },
            { char: '¼',  entity: '&frac14;' },  { char: '½', entity: '&frac12;' },  { char: '¾', entity: '&frac34;' },
            // Diakritische Zeichen
            { char: 'ˆ',  entity: '&circ;'   },  { char: '˜', entity: '&tilde;'  },
            // Interpunktionszeichen & Leerzeichen
            { char: ' ',  entity: '&nbsp;'   },  { char: '–', entity: '&ndash;'  },  { char: '—', entity: '&mdash;'  },
            // Währungen
            { char: '€',  entity: '&euro;'   },  { char: '¢', entity: '&cent;'   },  { char: '£', entity: '&pound;'  },
            // Umlaute
            { char: 'ä',  entity: '&auml;'   },  { char: 'ö', entity: '&ouml;'   },  { char: 'ü', entity: '&uuml;'   },
            { char: 'Ä',  entity: '&Auml;'   },  { char: 'Ö', entity: '&Ouml;'   },  { char: 'Ü', entity: '&Uuml;'   },
            { char: 'ß',  entity: '&szlig;'  }
        ];

        // Create Map for faster lookup
        const htmlEntityMap = new Map(htmlEntities.map(({ char, entity }) => [entity, char]));

        // Replace fixed Entities
        text = text.replace(/&[a-zA-Z0-9]+;/g, match => {
            return htmlEntityMap.get(match) || ' ';
        });

        // Replace Numerical and Hexadecimal Entities
        text = text.replace(/&#(\d+);/g, (match, dec) => String.fromCharCode(dec));
        text = text.replace(/&#x([a-fA-F0-9]+);/g, (match, hex) => {
            return String.fromCharCode(parseInt(hex, 16))
        });

        return text;
    }
}